import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";
import getAPIbaseURL, { toFormData } from "../../utils/helpers";
import i18n from "../../i18n";
const API_URL = `${getAPIbaseURL()}/absences`;

const withErrorDetail = (message, error) => {
  const detail = error.response?.data?.detail;
  return typeof detail === "string" ? `${message}: ${detail}` : message;
};

export const createAbsence = createAsyncThunk(
  "absences/createAbsence",
  async (absenceData, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      
      const payload = toFormData(absenceData);

      const response = await axios.post(
        `${API_URL}/`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      toast.error(withErrorDetail(i18n.t("slices.absencesSlice.error.createAbsence"), error));
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  }
);

export const fetchAbsences = createAsyncThunk(
  "absences/fetchAbsences",
  async (_, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error("Fetch absences error:", error.response?.data);
      return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.absencesSlice.error.fetchAbsences"));
    }
  }
);

export const fetchAbsenceBalance = createAsyncThunk(
  "absences/fetchAbsenceBalance",
  async (_, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/balance`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error("Fetch absence balance error:", error.response?.data);
      return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.absencesSlice.error.fetchAbsenceBalance"));
    }
  }
);

export const fetchHolidays = createAsyncThunk(
  "absences/fetchHolidays",
  async ({ year, userId, languageIsoCode } = {}, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const parsedYear = Number(year);
      const safeYear = !isNaN(parsedYear) && parsedYear > 0 ? parsedYear : new Date().getFullYear();

      const params = { year: safeYear };
      if (userId) params.user_id = userId;
      if (languageIsoCode) params.languageIsoCode = languageIsoCode;

      const res = await axios.get(`${API_URL}/holidays`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data;
    } catch (err) {
      console.error("Erro no fetchHolidays:", err.response?.data || err);
      return rejectWithValue(err.response?.data || i18n.t("slices.absencesSlice.error.fetchHolidays"));
    }
  }
);
export const fetchAllAbsenceBalances = createAsyncThunk(
  "absences/fetchAllAbsenceBalances",
  async (_, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/balances`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data; 
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.absencesSlice.error.fetchAllBalances"))
    }
  }
);


export const updateAbsenceStatus = createAsyncThunk(
  "absences/updateAbsenceStatus",
  async ({ absenceId, status, rejection_reason }, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${API_URL}/${absenceId}`,
        { 
          status, 
          rejection_reason: rejection_reason || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      refreshBalancesOfAbsenceOwner(thunkAPI, response.data);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  }
);

const refreshBalancesOfAbsenceOwner = ({ dispatch, getState }, absence) => {
  const userId = absence?.user_id;
  if (!userId) return;

  const year = new Date(absence.start_date).getFullYear();
  dispatch(fetchUserAbsenceBalance({ userId, year: Number.isNaN(year) ? undefined : year }));

  const state = getState();
  if (String(state.auth?.user?.id) === String(userId)) {
    dispatch(fetchAbsenceBalance());
  }
  if (String(state.absences?.selectedWorkerProfile?.id) === String(userId)) {
    dispatch(fetchWorkerProfile(userId));
  }
};

export const deleteAbsence = createAsyncThunk(
  "absences/deleteAbsence",
  async (absenceId, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/${absenceId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(i18n.t("slices.absencesSlice.success.deleted"));
      return absenceId;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.absencesSlice.error.deleteAbsence"));
    }
  }
);

export const filterAbsences = createAsyncThunk(
  "absences/filterAbsences",
  async (filters = {}, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams(filters).toString();
      const response = await axios.get(`${API_URL}/filter?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.absencesSlice.error.filterAbsences"));
    }
  }
);

export const fetchWorkerProfile = createAsyncThunk(
  "absences/fetchWorkerProfile",
  async (userId, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");

      const res = await axios.get(
        `${getAPIbaseURL()}/absences/profile/${userId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

export const fullUpdateAbsence = createAsyncThunk(
  "absences/fullUpdateAbsence",
  async ({ absenceId, updateData }, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      
      const payload = toFormData(updateData);

      const response = await axios.put(
        `${API_URL}/${absenceId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      toast.error(withErrorDetail(i18n.t("slices.absencesSlice.error.updateAbsence"), error));
      return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.absencesSlice.error.updateAbsence"));
    }
  }
);

export const fetchUserAbsenceBalance = createAsyncThunk(
  "absences/fetchUserAbsenceBalance",
  async ({ userId, year }, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/balances/${userId}`, {
        params: { year },
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.absencesSlice.error.fetchAbsenceBalance"));
    }
  }
);

export const fetchAbsenceAdjustments = createAsyncThunk(
  "absences/fetchAbsenceAdjustments",
  async ({ userId }, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/balances/${userId}/adjustments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return { userId, items: response.data };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.absencesSlice.error.fetchAdjustments"));
    }
  }
);

// Balances are never edited directly: an adjustment adds or subtracts days
// and the backend recalculates the balance.
export const createAbsenceAdjustment = createAsyncThunk(
  "absences/createAbsenceAdjustment",
  async ({ userId, cycle, target, operation, days, reason }, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/balances/${userId}/adjustments`,
        { cycle, target, operation, days, reason: reason || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(i18n.t("slices.absencesSlice.success.adjustmentCreated"));
      return { userId, adjustment: response.data };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.absencesSlice.error.createAdjustment"));
    }
  }
);

export const fetchTeamOverview = createAsyncThunk(
  "absences/fetchTeamOverview",
  async ({ year, month, scope = "all", scopeId, search } = {}, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const params = { year, month, scope };
      if (scopeId) params.scope_id = scopeId;
      if (search?.trim()) params.search = search.trim();

      const response = await axios.get(`${API_URL}/team-overview`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.absencesSlice.error.fetchTeamOverview"));
    }
  }
);

const syncUserBalance = (state, balance) => {
  state.userBalance = balance;
  if (balance.year === new Date().getFullYear()) {
    state.balancesByUserId[balance.user_id] = { ...state.balancesByUserId[balance.user_id], ...balance };
  }
};

const absencesSlice = createSlice({
  name: "absences",
  initialState: {
    items: [],
    holidays: [],
    workerHolidays: [],    
    currentUser: null,
    selectedWorkerProfile: null,
    isLoading: false,
    error: null,
    preferences: null,
    profilePiscturePath: null,
    message: null,
    balance: null,
    balancesByUserId: {},
    userBalance: null,
    userAdjustments: null,
    teamOverview: { users: [], isLoading: false, error: null, requestId: null },
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTeamOverview.pending, (state, action) => {
        state.teamOverview.isLoading = true;
        state.teamOverview.error = null;
        state.teamOverview.requestId = action.meta.requestId;
      })
      .addCase(fetchTeamOverview.fulfilled, (state, action) => {
        if (state.teamOverview.requestId !== action.meta.requestId) return;
        state.teamOverview.isLoading = false;
        state.teamOverview.users = action.payload.users;
      })
      .addCase(fetchTeamOverview.rejected, (state, action) => {
        if (state.teamOverview.requestId !== action.meta.requestId) return;
        state.teamOverview.isLoading = false;
        state.teamOverview.error = action.payload?.detail || i18n.t("slices.absencesSlice.error.fetchTeamOverview");
      })
      .addCase(fetchUserAbsenceBalance.fulfilled, (state, action) => {
        syncUserBalance(state, action.payload);
      })
      .addCase(fetchAbsenceAdjustments.fulfilled, (state, action) => {
        state.userAdjustments = action.payload;
      })
      .addCase(createAbsenceAdjustment.fulfilled, (state, action) => {
        const { userId, adjustment } = action.payload;
        if (String(state.userAdjustments?.userId) === String(userId)) {
          state.userAdjustments.items = [adjustment, ...state.userAdjustments.items];
        }
      })
      .addCase(createAbsence.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createAbsence.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items.push(action.payload);
        state.message = i18n.t("slices.absencesSlice.success.created");
      })
      .addCase(createAbsence.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchAbsences.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAbsences.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload;
      })
      .addCase(fetchAbsences.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.detail || i18n.t("slices.absencesSlice.error.genericFetch");
      })
      .addCase(fetchAbsenceBalance.fulfilled, (state, action) => {
        state.balance = action.payload;
      })
      .addCase(fetchAllAbsenceBalances.fulfilled, (state, action) => {
        state.balancesByUserId = {};
        action.payload.forEach(b => {
          state.balancesByUserId[b.user_id] = b;
        });
      })
      .addCase(updateAbsenceStatus.fulfilled, (state, action) => {
        const updatedAbsence = action.payload;
        const index = state.items.findIndex((a) => a.id === updatedAbsence.id);
        if (index !== -1) {
          state.items[index] = { ...state.items[index], ...updatedAbsence };
        }
        state.message = i18n.t("slices.absencesSlice.success.statusUpdated");
      })
      .addCase(filterAbsences.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(filterAbsences.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload;
      })
      .addCase(filterAbsences.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(fullUpdateAbsence.fulfilled, (state, action) => {
        const updated = action.payload;
        const index = state.items.findIndex((a) => a.id === updated.id);
        if (index !== -1) {
          state.items[index] = updated;
        }
        state.message = i18n.t("slices.absencesSlice.success.fullyUpdated");
      })
      .addCase(deleteAbsence.fulfilled, (state, action) => {
        state.items = state.items.filter((a) => a.id !== action.payload);
        state.message = i18n.t("slices.absencesSlice.success.deleted");
      })
      .addCase(fetchWorkerProfile.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchWorkerProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedWorkerProfile = action.payload;
      })
      .addCase(fetchWorkerProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchHolidays.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchHolidays.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.meta.arg?.userId) {
          state.workerHolidays = action.payload;
        } else {
          state.holidays = action.payload;
        }
      })
      .addCase(fetchHolidays.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
  },
});

export default absencesSlice.reducer;